/**
 * COMPANY AUTHENTICATION SERVICE
 *
 * Handles company account login with username/password authentication.
 * Separate from individual user authentication flow.
 *
 * Features:
 * - Credential setup for company accounts
 * - Password hashing with PBKDF2-SHA256
 * - Session token management
 * - Failed login tracking and account locking
 * - Password requirements validation
 */

import type {
 CreateCompanyCredentialsInput,
 CompanyLoginRequest,
 CompanyLoginResponse,
 CompanySessionValidation,
 CompanyAccountSummary
} from '../../types/company';

// =============================================================================
// CONSTANTS (kept for client-side validation)
// =============================================================================

const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 128;
const USERNAME_MIN_LENGTH = 4;
const USERNAME_MAX_LENGTH = 50;

// =============================================================================
// API HELPER
// =============================================================================

/**
 * Helper function to call the company auth API endpoint
 */
async function callCompanyAuthAPI<T = any>(operation: string, data: Record<string, any> = {}): Promise<T> {
 const response = await fetch('/api/user/company-auth', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ operation, data })
 });

 const result = await response.json();

 if (!response.ok) {
 throw new Error(result.error || `API error: ${response.status}`);
 }

 return result;
}

// =============================================================================
// CLIENT-SIDE VALIDATION (for quick UI feedback before API call)
// =============================================================================

/**
 * Validate password complexity requirements
 */
export function validatePasswordRequirements(password: string): { isValid: boolean; errors: string[] } {
 const errors: string[] = [];

 if (password.length < PASSWORD_MIN_LENGTH) {
 errors.push(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
 }
 if (password.length > PASSWORD_MAX_LENGTH) {
 errors.push(`Password must be at most ${PASSWORD_MAX_LENGTH} characters`);
 }
 if (!/[A-Z]/.test(password)) {
 errors.push('Password must contain at least one uppercase letter');
 }
 if (!/[a-z]/.test(password)) {
 errors.push('Password must contain at least one lowercase letter');
 }
 if (!/[0-9]/.test(password)) {
 errors.push('Password must contain at least one number');
 }
 if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
 errors.push('Password must contain at least one special character');
 }

 return { isValid: errors.length === 0, errors };
}

/**
 * Validate username requirements
 */
export function validateUsernameRequirements(username: string): { isValid: boolean; errors: string[] } {
 const errors: string[] = [];

 if (username.length < USERNAME_MIN_LENGTH) {
 errors.push(`Username must be at least ${USERNAME_MIN_LENGTH} characters`);
 }
 if (username.length > USERNAME_MAX_LENGTH) {
 errors.push(`Username must be at most ${USERNAME_MAX_LENGTH} characters`);
 }
 if (!/^[a-zA-Z0-9_]+$/.test(username)) {
 errors.push('Username can only contain letters, numbers, and underscores');
 }

 return { isValid: errors.length === 0, errors };
}

// =============================================================================
// MAIN SERVICE FUNCTIONS (all use API calls)
// =============================================================================

/**
 * Check if a company already has credentials set up
 */
export async function hasCompanyCredentials(companyId: string): Promise<boolean> {
 try {
 const result = await callCompanyAuthAPI<{ hasCredentials: boolean }>('has_credentials', { companyId });
 return result.hasCredentials;
 } catch (error) {
 console.error('[COMPANY-AUTH] Error checking credentials:', error);
 return false;
 }
}

/**
 * Check if a username is already taken
 */
export async function isUsernameTaken(username: string): Promise<boolean> {
 try {
 const result = await callCompanyAuthAPI<{ isTaken: boolean }>('is_username_taken', { username });
 return result.isTaken;
 } catch (error) {
 console.error('[COMPANY-AUTH] Error checking username:', error);
 return false;
 }
}

/**
 * Create login credentials for a company account
 */
export async function createCompanyCredentials(
 input: CreateCompanyCredentialsInput
): Promise<{ success: boolean; error?: string }> {
 // Client-side validation for quick UI feedback
 const usernameValidation = validateUsernameRequirements(input.username);
 if (!usernameValidation.isValid) {
 return { success: false, error: usernameValidation.errors.join('. ') };
 }

 const passwordValidation = validatePasswordRequirements(input.password);
 if (!passwordValidation.isValid) {
 return { success: false, error: passwordValidation.errors.join('. ') };
 }

 try {
 const result = await callCompanyAuthAPI<{ success: boolean; error?: string }>('create_credentials', {
 companyId: input.companyId,
 username: input.username,
 password: input.password,
 setupByUserId: input.setupByUserId
 });
 return result;
 } catch (error: any) {
 console.error('[COMPANY-AUTH] Error creating credentials:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
}

/**
 * Authenticate company login
 */
export async function loginCompany(request: CompanyLoginRequest): Promise<CompanyLoginResponse> {
 try {
 const result = await callCompanyAuthAPI<CompanyLoginResponse>('login', {
 username: request.username,
 password: request.password
 });
 return result;
 } catch (error: any) {
 console.error('[COMPANY-AUTH] Login error:', error);
 return { success: false, error: error.message || 'An unexpected error occurred during login' };
 }
}

/**
 * Validate a company session token
 */
export async function validateCompanySession(sessionToken: string): Promise<CompanySessionValidation> {
 try {
 const result = await callCompanyAuthAPI<CompanySessionValidation>('validate_session', { sessionToken });
 return result;
 } catch (error: any) {
 console.error('[COMPANY-AUTH] Session validation error:', error);
 return { isValid: false, error: 'Failed to validate session' };
 }
}

/**
 * Logout company - invalidate session token
 */
export async function logoutCompany(sessionToken: string): Promise<{ success: boolean }> {
 try {
 const result = await callCompanyAuthAPI<{ success: boolean }>('logout', { sessionToken });
 return result;
 } catch (error: any) {
 console.error('[COMPANY-AUTH] Logout error:', error);
 return { success: false };
 }
}

/**
 * Change company password
 */
export async function changeCompanyPassword(
 companyId: string,
 currentPassword: string,
 newPassword: string
): Promise<{ success: boolean; error?: string }> {
 // Client-side validation for quick UI feedback
 const validation = validatePasswordRequirements(newPassword);
 if (!validation.isValid) {
 return { success: false, error: validation.errors.join('. ') };
 }

 try {
 const result = await callCompanyAuthAPI<{ success: boolean; error?: string }>('change_password', {
 companyId,
 currentPassword,
 newPassword
 });
 return result;
 } catch (error: any) {
 console.error('[COMPANY-AUTH] Password change error:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
}

/**
 * Get credential status for a company (for UI display)
 */
export async function getCompanyCredentialStatus(companyId: string): Promise<{
 hasCredentials: boolean;
 username?: string;
 lastLoginAt?: string;
 isActive?: boolean;
}> {
 try {
 const result = await callCompanyAuthAPI<{
 hasCredentials: boolean;
 username?: string;
 lastLoginAt?: string;
 isActive?: boolean;
 }>('get_credential_status', { companyId });
 return result;
 } catch (error: any) {
 console.error('[COMPANY-AUTH] Error getting credential status:', error);
 return { hasCredentials: false };
 }
}

